import { Box, Button, Field, Grid, Heading, HStack, Image, Input, Portal, Text, Tooltip, VStack } from '@chakra-ui/react'
import { useRef, useState } from 'react'
import { LeadPayload, PilotAssessmentResult, PilotSupportItem } from '../pilotTypes'
import { StreetViewImage } from '../../components/common/StreetViewImage'
import PilotLeadCapture from './PilotLeadCapture'

interface PilotResultsProps {
  result: PilotAssessmentResult
  partnerName: string
  isLandlord: boolean
  showLandlordEmail: boolean
  mortgageOrBankProvider?: string
  supportAvailable: PilotSupportItem[]
  pricesUnlocked: boolean
  onGetPricesStart: () => void
  onSubmitLead: (payload: LeadPayload) => Promise<void>
  isSubmittingLead: boolean
  leadError?: string | null
  onNewAddress: () => void
  onBack: () => void
  onSendLandlordEmail?: (email: string) => void
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const WARM_HOMES_LOCAL_GRANT_PATTERN = /warm homes:?\s+local grant/i
const WARM_HOMES_LOCAL_GRANT_URL = 'https://www.gov.uk/apply-warm-homes-local-grant'

interface GreenHomeCashbackScheme {
  schemeName: string
  amountGbp: number
  url: string
}

const GREEN_HOME_CASHBACK_BY_BANK: Record<string, GreenHomeCashbackScheme | null> = {
  'Lloyds Bank': {
    schemeName: 'Lloyds Bank Eco Home Reward',
    amountGbp: 2000,
    url: 'https://www.lloydsbank.com/mortgages/help-and-guidance/eco-home-hub/eco-home-reward.html',
  },
  'NatWest': null,
  'Nationwide': null,
  'HSBC UK': null,
  'Bank of Scotland / Halifax': {
    schemeName: 'Halifax Green Living Reward',
    amountGbp: 2000,
    url: 'https://www.halifax.co.uk/mortgages/help-and-advice/green-living/green-living-offers.html',
  },
  'Barclays': {
    schemeName: 'Barclays Greener Home Reward',
    amountGbp: 2000,
    url: 'https://www.barclays.co.uk/mortgages/greener-home-reward/',
  },
  'Santander': null,
  'Royal Bank of Scotland': null,
  'Virgin Money': null,
  'TSB': null,
  'Prefer not to say': null,
}

const eligibleCashbackBanks = Object.entries(GREEN_HOME_CASHBACK_BY_BANK)
  .filter(([, scheme]) => scheme != null)
  .map(([bank]) => bank)
  .join(', ')

const NATIONWIDE_GREEN_ADDITIONAL_BORROWING = {
  title: 'Green Additional Borrowing with 0% interest',
  amount: '£5,000 to £20,000',
  url: 'https://www.nationwide.co.uk/mortgages/borrowing-more/green-additional-borrowing/',
}

function HelpTip({ label }: { label: string }) {
  return (
    <Tooltip.Root openDelay={100} closeDelay={100}>
      <Tooltip.Trigger asChild>
        <Box
          as="button"
          type="button"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          w="14px"
          h="14px"
          borderRadius="full"
          border="1px solid"
          borderColor="gray.300"
          color="gray.400"
          fontSize="9px"
          fontWeight="bold"
          lineHeight={1}
          cursor="help"
          flexShrink={0}
          _hover={{ borderColor: 'gray.500', color: 'gray.600' }}
          aria-label={`About: ${label}`}
        >
          ?
        </Box>
      </Tooltip.Trigger>
      <Portal>
        <Tooltip.Positioner>
          <Tooltip.Content maxW="240px" fontSize="xs" lineHeight={1.5}>
            {label}
          </Tooltip.Content>
        </Tooltip.Positioner>
      </Portal>
    </Tooltip.Root>
  )
}

function SupportThumbnail({ thumbnailUrl }: { thumbnailUrl?: string }) {
  const [hasImageError, setHasImageError] = useState(false)

  if (!thumbnailUrl || hasImageError) {
    return (
      <Box
        w={{ base: '48px', sm: '64px' }}
        h={{ base: '48px', sm: '64px' }}
        flexShrink={0}
        bg="gray.100"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
      />
    )
  }

  return (
    <Image
      src={thumbnailUrl}
      alt=""
      w={{ base: '48px', sm: '64px' }}
      h={{ base: '48px', sm: '64px' }}
      flexShrink={0}
      objectFit="cover"
      borderRadius="md"
      onError={() => setHasImageError(true)}
    />
  )
}

function EpcBadge({ rating, size = 'md' }: { rating?: string; size?: 'sm' | 'md' | 'lg' }) {
  const colors: Record<string, { bg: string; color: string }> = {
    A: { bg: '#008000', color: 'white' },
    B: { bg: '#2CA800', color: 'white' },
    C: { bg: '#75BC00', color: 'white' },
    D: { bg: '#FFD500', color: 'gray.800' },
    E: { bg: '#FFB000', color: 'white' },
    F: { bg: '#FF7800', color: 'white' },
    G: { bg: '#E00000', color: 'white' },
  }
  const r = rating?.toUpperCase() ?? '?'
  const style = colors[r] ?? { bg: 'gray.300', color: 'gray.700' }
  const dims = size === 'lg' ? { w: '48px', h: '48px', fontSize: 'xl' } : { w: '36px', h: '36px', fontSize: 'lg' }
  return (
    <Box
      display="inline-flex" alignItems="center" justifyContent="center"
      borderRadius="md"
      bg={style.bg} color={style.color}
      fontWeight="bold"
      {...dims}
    >
      {r}
    </Box>
  )
}

// Placeholder shown in place of a price until the user submits their contact
// details. The real figure is deliberately not rendered (not even blurred), so
// it can't be recovered from the DOM.
function LockedValue({
  placeholder,
  fontSize = 'sm',
  onReveal,
}: {
  placeholder: string
  fontSize?: string | Record<string, string>
  onReveal: () => void
}) {
  return (
    <Box
      as="button"
      type="button"
      display="inline-flex"
      alignItems="center"
      gap="6px"
      cursor="pointer"
      onClick={onReveal}
      title="Get your free price estimates to see this figure"
      aria-label="Locked — get your free price estimates to see this figure"
    >
      <Text
        fontSize={fontSize}
        fontWeight="bold"
        color="gray.900"
        filter="blur(5px)"
        userSelect="none"
        aria-hidden
      >
        {placeholder}
      </Text>
      <Text fontSize="sm" lineHeight={1} aria-hidden>🔒</Text>
    </Box>
  )
}

function fmt(n: number | null | undefined, prefix = '', suffix = '', fallback = '–') {
  if (n == null) return fallback
  return `${prefix}${Math.round(n).toLocaleString()}${suffix}`
}

function hasFiniteNumber(value?: number | null): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function normalizeEpcBand(value?: string | null): string | null {
  const band = value?.trim().toUpperCase()
  return band && /^[A-G]$/.test(band) ? band : null
}

function cleanAgeBand(band?: string) {
  if (!band) return ''
  return band.replace(/^[^:]+:\s*/, '')
}

function formatPayback(years?: number | null): string {
  if (years == null || !Number.isFinite(years) || years < 0) return '–'
  const totalMonths = Math.round(years * 12)
  if (totalMonths <= 0) return 'Under 1 month'
  const y = Math.floor(totalMonths / 12)
  const m = totalMonths % 12
  if (y === 0) return m === 1 ? '1 month' : `${m} months`
  if (m === 0) return y === 1 ? '1 year' : `${y} years`
  return `${y} ${y === 1 ? 'year' : 'years'} ${m} ${m === 1 ? 'month' : 'months'}`
}

function formatRoi(roi?: number | null): string {
  if (roi == null || !Number.isFinite(roi) || roi < 0) return '–'
  return `${roi.toLocaleString('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
}

const PilotResults: React.FC<PilotResultsProps> = ({
  result,
  partnerName,
  isLandlord,
  showLandlordEmail,
  mortgageOrBankProvider,
  supportAvailable,
  pricesUnlocked,
  onGetPricesStart,
  onSubmitLead,
  isSubmittingLead,
  leadError,
  onNewAddress,
  onBack,
  onSendLandlordEmail,
}) => {
  const { home, grants, tenantPower } = result
  const features = home.features ?? {}
  const [landlordEmail, setLandlordEmail] = useState('')
  const [landlordEmailError, setLandlordEmailError] = useState<string | null>(null)
  const [leadFormOpen, setLeadFormOpen] = useState(false)
  const leadCaptureRef = useRef<HTMLDivElement | null>(null)

  const revealLeadForm = () => {
    if (!leadFormOpen) {
      setLeadFormOpen(true)
      onGetPricesStart()
    }
    leadCaptureRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const propertyType = features.propertyType ?? ''
  const builtForm = features.builtForm ?? ''
  const ageBand = cleanAgeBand(features.constructionAgeBand)
  const propertyLine = [propertyType, builtForm, ageBand].filter(Boolean).join(' · ')

  const currentRating = features.currentEpcRating
  const potentialRating = features.potentialEpcRating
  const noEpcFound = !(home.lmkKey?.trim() || currentRating?.trim())
  const availableCurrentEpcBand = normalizeEpcBand(currentRating)
  const epcUpliftCurrentBand = normalizeEpcBand(
    home.estimatedEpcAfterUpgrades?.currentBand,
  ) ?? availableCurrentEpcBand
  const epcUpliftEstimatedBand = normalizeEpcBand(
    home.estimatedEpcAfterUpgrades?.estimatedBand,
  )
  const showLandlordEpcUplift = isLandlord
    && availableCurrentEpcBand != null
    && epcUpliftCurrentBand != null
    && epcUpliftEstimatedBand != null

  const isElectricHeating = /electric|heat pump/i.test(features.primaryHeating ?? '')
  const heatingFuelEmoji = isElectricHeating ? '⚡' : '🔥'
  const nonHeatingGasKwh = home.nonHeatingGasKwh ?? null

  const gasKwh = (!isElectricHeating ? (home.spaceHeatingDemandKwh ?? 0) : 0) + (nonHeatingGasKwh ?? 0)
  const elecKwh = (isElectricHeating ? (home.spaceHeatingDemandKwh ?? 0) : 0) + (home.nonHeatingElectricityKwh ?? 0)
  const totalGasKwh = gasKwh > 0 ? gasKwh : null
  const totalElecKwh = elecKwh > 0 ? elecKwh : null
  const greenerHomeCashbackScheme = mortgageOrBankProvider
    ? GREEN_HOME_CASHBACK_BY_BANK[mortgageOrBankProvider] ?? null
    : null
  const hasGreenerHomeCashback = greenerHomeCashbackScheme != null
  const greenerHomeCashbackAmount = greenerHomeCashbackScheme?.amountGbp ?? 0
  const hasNationwideGreenAdditionalBorrowing = mortgageOrBankProvider === 'Nationwide'

  const ELECTRIFICATION_COP = 3.2
  const electrifiedHeatKwh = home.spaceHeatingDemandKwh != null ? home.spaceHeatingDemandKwh / ELECTRIFICATION_COP : null
  const electrifiedNonHeatingGasKwh = nonHeatingGasKwh != null ? nonHeatingGasKwh / ELECTRIFICATION_COP : 0
  const electrifiedTotalKwh =
    electrifiedHeatKwh != null || home.nonHeatingElectricityKwh != null
      ? (electrifiedHeatKwh ?? 0) + (home.nonHeatingElectricityKwh ?? 0) + electrifiedNonHeatingGasKwh
      : null

  const hpKw = home.upgradesHeatPumpKw != null && home.upgradesHeatPumpKw > 0
    ? home.upgradesHeatPumpKw
    : null
  const cylinderLitres = home.upgradesCylinderLitres != null && home.upgradesCylinderLitres > 0
    ? home.upgradesCylinderLitres
    : null
  // Quoted sizes are the ones the installer actually sells (4, 6.5, 9, 12,
  // 15.5 kW), so drop the trailing zero on whole sizes: "9 kW", not "9.0 kW".
  const hpKwLabel = hpKw != null
    ? `${Number.isInteger(hpKw) ? hpKw : hpKw.toFixed(1)} kW`
    : null
  const heatPumpSubline = hpKwLabel == null
    ? 'Uses electricity instead of a gas boiler'
    : cylinderLitres != null
      ? `${hpKwLabel} heat pump + ${cylinderLitres}L hot water cylinder`
      : `${hpKwLabel} heat pump`
  const solarCost = home.upgradesSolarPvCost ?? null
  const batteryCost = home.upgradesBatteryCost ?? null
  const eligibleBoilerUpgradeSchemeGrant = grants
    .filter(grant => ['yes', 'unknown'].includes(grant.eligible) && /boiler upgrade scheme/i.test(grant.name))
    .reduce((maxAmount, grant) => Math.max(maxAmount, grant.maxAmountGbp ?? 0), 0)
  const heatPumpGrant = Math.max(home.upgradesHeatPumpGrant ?? 0, eligibleBoilerUpgradeSchemeGrant)
  const heatPumpGrossCost = home.upgradesHeatPumpCost != null && home.upgradesHeatPumpCost > 0
    ? home.upgradesHeatPumpCost
    : null
  const heatPumpGrantApplied = heatPumpGrossCost != null
    ? Math.min(heatPumpGrant, heatPumpGrossCost)
    : heatPumpGrant
  const heatPumpNetCostBeforeCashback = heatPumpGrossCost != null ? Math.max(heatPumpGrossCost - heatPumpGrantApplied, 0) : null
  const netUpgradeCostBeforeCashback = home.upgradesTotalCost != null
    ? Math.max(home.upgradesTotalCost - heatPumpGrantApplied, 0)
    : null
  const greenerHomeCashbackApplied = netUpgradeCostBeforeCashback != null
    ? Math.min(greenerHomeCashbackAmount, netUpgradeCostBeforeCashback)
    : greenerHomeCashbackAmount
  const heatPumpCashbackApplied = heatPumpNetCostBeforeCashback != null
    ? Math.min(greenerHomeCashbackApplied, heatPumpNetCostBeforeCashback)
    : 0
  const heatPumpNetCost = heatPumpNetCostBeforeCashback != null
    ? Math.max(heatPumpNetCostBeforeCashback - heatPumpCashbackApplied, 0)
    : null
  const netUpgradeCost = netUpgradeCostBeforeCashback != null
    ? Math.max(netUpgradeCostBeforeCashback - greenerHomeCashbackApplied, 0)
    : null
  const frontendPaybackPeriod =
    netUpgradeCost != null && hasFiniteNumber(home.upgradesAnnualSavings) && home.upgradesAnnualSavings > 0
      ? netUpgradeCost / home.upgradesAnnualSavings
      : home.upgradesPaybackPeriod
  const frontendRoi =
    netUpgradeCost != null && netUpgradeCost > 0 && hasFiniteNumber(home.upgradesAnnualSavings)
      ? (home.upgradesAnnualSavings / netUpgradeCost) * 100
      : home.upgradesRoi
  const tenantPowerAnnualPayment = tenantPower?.investor.totalAnnualPayment
  const tenantPowerRoi =
    netUpgradeCost != null && netUpgradeCost > 0
      && hasFiniteNumber(tenantPowerAnnualPayment) && tenantPowerAnnualPayment > 0
      ? (tenantPowerAnnualPayment / netUpgradeCost) * 100
      : tenantPower?.investor.roi
  const tenantPowerPaybackPeriod =
    netUpgradeCost != null && netUpgradeCost > 0
      && hasFiniteNumber(tenantPowerAnnualPayment) && tenantPowerAnnualPayment > 0
      ? netUpgradeCost / tenantPowerAnnualPayment
      : tenantPower?.investor.paybackPeriod
  const upgradeCostDeductions = [
    heatPumpGrantApplied > 0 ? `${fmt(heatPumpGrantApplied, '£')} heat pump grant` : null,
    greenerHomeCashbackApplied > 0 ? `${fmt(greenerHomeCashbackApplied, '£')} cashback` : null,
  ].filter(Boolean)
  const upgradeCostHelp = [
    `This starts with the estimated total upgrade cost before support: ${fmt(home.upgradesTotalCost, '£')}.`,
    upgradeCostDeductions.length > 0
      ? `We then subtract ${upgradeCostDeductions.join(' and ')}.`
      : 'No grants or cashback have been deducted from this estimate.',
    `That gives the upgrade cost shown here: ${fmt(netUpgradeCost, '£')}.`,
    'Exact costs and eligibility will be confirmed during your consultation.',
  ].join(' ')

  const billReductionPercent =
    home.upgradesCurrentEnergyCost != null && home.upgradesCurrentEnergyCost > 0 && home.upgradesEnergyCostAfter != null
      ? Math.max(0, Math.round(((home.upgradesCurrentEnergyCost - home.upgradesEnergyCostAfter) / home.upgradesCurrentEnergyCost) * 100))
      : null
  const hasCoreUpgradeFigures = [
    home.upgradesCurrentEnergyCost,
    home.upgradesEnergyCostAfter,
    home.upgradesAnnualSavings,
    home.upgradesTotalCost,
  ].every(hasFiniteNumber)
  const isAssessmentUnavailable = !hasCoreUpgradeFigures


  const handleLandlordEmailSubmit = () => {
    const email = landlordEmail.trim()
    if (!EMAIL_PATTERN.test(email)) {
      setLandlordEmailError('Enter a valid email address.')
      return
    }

    setLandlordEmailError(null)
    onSendLandlordEmail?.(email)
  }

  if (isAssessmentUnavailable) {
    return (
      <Box maxW="720px" mx="auto" py={{ base: 6, sm: 10 }} px={{ base: 3, sm: 4 }}>
        <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" overflow="hidden">
          <Box px={{ base: 4, sm: 6 }} py={{ base: 5, sm: 6 }}>
            <VStack align="stretch" gap={5}>
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="0"
                  mb={2}
                >
                  Assessment unavailable
                </Text>
                <Heading size="md" color="gray.900">We couldn't complete this assessment</Heading>
                <Text fontSize="sm" color="gray.600" mt={3} lineHeight={1.6}>
                  We couldn't find enough reliable information about this property to estimate the upgrade costs,
                  energy savings, or payback period.
                </Text>
              </Box>

              <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="lg" p={4}>
                <Text fontSize="xs" color="gray.500" mb={1}>Address checked</Text>
                <Text fontSize="sm" fontWeight="semibold" color="gray.800">{home.fullAddress}</Text>
                <Text fontSize="xs" color="gray.500" mt={2} lineHeight={1.5}>
                  This can happen when the public records for a home are incomplete or don't include the data needed
                  to model improvements accurately.
                </Text>
              </Box>

              <HStack gap={3} justify="flex-start" flexWrap="wrap" flexDirection={{ base: 'column', sm: 'row' }}>
                <Button bg="brand.500" color="white" _hover={{ opacity: 0.9 }} onClick={onNewAddress} w={{ base: 'full', sm: 'auto' }}>
                  Try another address
                </Button>
                <Button
                  bg="white"
                  color="gray.700"
                  border="1px solid"
                  borderColor="gray.200"
                  _hover={{ bg: 'gray.50', borderColor: 'gray.300' }}
                  onClick={onBack}
                  w={{ base: 'full', sm: 'auto' }}
                >
                  Back to address
                </Button>
              </HStack>
            </VStack>
          </Box>
        </Box>
      </Box>
    )
  }


  // The single lead-capture card: pitch → inline form → unlocked confirmation.
  // Locked prices elsewhere on the page scroll back to it via revealLeadForm.
  const leadCapture = (
    <Box ref={leadCaptureRef} scrollMarginTop="16px">
      <PilotLeadCapture
        partnerName={partnerName}
        homeAddress={home.fullAddress}
        homeId={home.id}
        unlocked={pricesUnlocked}
        formOpen={leadFormOpen}
        onOpenForm={revealLeadForm}
        onSubmit={onSubmitLead}
        isSubmitting={isSubmittingLead}
        apiError={leadError}
      />
    </Box>
  )

  return (
    <Box maxW="860px" mx="auto" py={{ base: 4, sm: 8 }} px={{ base: 3, sm: 4 }}>
      <VStack gap={4} align="stretch">

        {/* Card 1: Property identity */}
        <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" overflow="hidden">
          <HStack gap={0} align="stretch" flexDirection={{ base: 'column', sm: 'row' }}>
            <Box
              w={{ base: 'full', sm: '130px' }}
              minW={{ base: 'full', sm: '130px' }}
              h={{ base: '140px', sm: 'auto' }}
              flexShrink={0}
              overflow="hidden"
            >
              <StreetViewImage
                imageUrl={home.imageUrl}
                address={home.fullAddress}
                svSize="400x300"
                alt={`Street view of ${home.fullAddress}`}
                w="100%" h="100%" objectFit="cover" minH="80px"
              />
            </Box>
            <Box flex={1} px={{ base: 4, sm: 5 }} py={4} display="flex" alignItems="center" minW={0}>
              <Box minW={0}>
                <Text fontWeight="bold" fontSize="md" color="gray.800" mb={0.5} overflowWrap="anywhere">{home.fullAddress}</Text>
                {propertyLine && <Text fontSize="sm" color="gray.500">{propertyLine}</Text>}
              </Box>
            </Box>
            <VStack
              justify="center"
              align="center"
              px={{ base: 4, sm: 5 }}
              py={{ base: 3, sm: 4 }}
              gap={{ base: 2, sm: 1 }}
              flexShrink={0}
              flexDirection={{ base: 'row', sm: 'column' }}
              flexWrap={{ base: 'wrap', sm: 'nowrap' }}
              borderTopWidth={{ base: '1px', sm: '0' }}
              borderColor="gray.100"
            >
              <HStack gap={1}>
                <Text fontSize="xs" color="gray.400" textAlign="center">Current EPC</Text>
                <HelpTip label="Your Energy Performance Certificate rating, from A (most efficient) to G (least efficient). Based on the latest assessment held by the government." />
              </HStack>
              {noEpcFound ? (
                <Box
                  bg="orange.50"
                  border="1px solid"
                  borderColor="orange.200"
                  borderRadius="md"
                  px={3}
                  py={2}
                >
                  <Text fontSize="sm" fontWeight="bold" color="orange.700" whiteSpace="nowrap">
                    No EPC
                  </Text>
                </Box>
              ) : (
                <>
                  <EpcBadge rating={currentRating} size="lg" />
                  {potentialRating && potentialRating !== currentRating && (
                    <>
                      <Text fontSize="xs" color="gray.300">→</Text>
                      <HStack gap={1}>
                        <Text fontSize="xs" color="gray.400" textAlign="center">After retrofit</Text>
                        <HelpTip label="The EPC rating your home could achieve once the recommended upgrades are in place, according to government modelling." />
                      </HStack>
                      <EpcBadge rating={potentialRating} size="lg" />
                    </>
                  )}
                </>
              )}
            </VStack>
          </HStack>
        </Box>

        {/* Card 2: Hero + At a glance */}
        <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" overflow="hidden">
          <Box px={{ base: 4, sm: 5 }} py={3} borderBottom="1px solid" borderColor="gray.100" bg="gray.50">
            <HStack justify="space-between" flexDirection={{ base: 'column', sm: 'row' }} align={{ base: 'flex-start', sm: 'center' }} gap={2}>
              <Heading size="sm" color="gray.800">Your retrofit assessment</Heading>
              <HStack gap={2} align="center">
                <Text fontSize="xs" color="gray.400">powered by</Text>
                <a href="https://greenflip.homes" target="_blank" rel="noopener noreferrer">
                  <Image src="/greenflip_logo.png" h="16px" alt="Greenflip" style={{ verticalAlign: 'middle' }} />
                </a>
              </HStack>
            </HStack>
          </Box>

          {/* Narrative body */}
          <Box px={{ base: 4, sm: 8 }} py={{ base: 7, sm: 10 }} textAlign="center">
            <HStack gap={2} align="baseline" justify="center" flexWrap="wrap">
              <Text fontSize={{ base: 'md', sm: 'lg' }} color="gray.600">You could save up to</Text>
              {home.upgradesAnnualSavings != null && (
                <Text fontSize={{ base: '3xl', sm: '5xl' }} fontWeight="extrabold" color="green.600" lineHeight={1}>
                  {fmt(home.upgradesAnnualSavings, '£')}
                </Text>
              )}
              {billReductionPercent != null ? (
                <>
                  <Text fontSize={{ base: 'md', sm: 'lg' }} color="gray.600">annually, that's </Text>
                  <Text fontSize={{ base: '3xl', sm: '5xl' }} fontWeight="extrabold" color="green.600" lineHeight={1}>
                    {billReductionPercent}%
                  </Text>
                  <Text fontSize={{ base: 'md', sm: 'lg' }} color="gray.600">off your energy bills.</Text>
                </>
              ) : (
                <Text fontSize={{ base: 'md', sm: 'lg' }} color="gray.600">annually.</Text>
              )}
            </HStack>
            <Text fontSize="md" color="gray.400" mt={4}>Let's see how.</Text>
          </Box>
        </Box>

        {/* Section 2: Current energy use */}
        <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" overflow="hidden">
          <Box px={{ base: 4, sm: 5 }} pt={{ base: 5, sm: 6 }} pb={4}>
            <Heading size="md" color="gray.900">Your current energy use</Heading>
            <Text fontSize="sm" color="gray.500" mt={1} lineHeight={1.6}>
              Based on the available information, here is what your home should be using in energy annually.
            </Text>
          </Box>
          <Box px={{ base: 4, sm: 5 }} pb={5}>
            <VStack gap={2} align="stretch">
              {home.spaceHeatingDemandKwh != null && (
                <Box bg="gray.100" borderRadius="lg" p={{ base: 3, sm: 4 }}>
                  <HStack gap={{ base: 3, sm: 4 }} align="center" flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
                    <Box
                      w="40px" h="40px" flexShrink={0}
                      display="flex" alignItems="center" justifyContent="center"
                      borderRadius="xl" bg="white" boxShadow="xs"
                      fontSize="xl" lineHeight={1}
                    >
                      🌡️
                    </Box>
                    <Box flex={1} minW={0}>
                      <HStack gap={1.5} mb={0.5}>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">Space heating</Text>
                        <HelpTip label="How much energy your home needs to stay warm in a typical year. This is the biggest factor in most energy bills and depends on how well insulated your home is." />
                      </HStack>
                      <Text fontSize="xs" color="gray.500">Heating your home comfortably at 20C&deg; during colder days</Text>
                      {/* {features.primaryHeating && (
                        <Text fontSize="xs" color="gray.500">{features.primaryHeating}</Text>
                      )} */}
                    </Box>
                    <Text
                      fontSize="sm"
                      fontWeight="bold"
                      color="gray.900"
                      flexShrink={0}
                      w={{ base: 'full', sm: 'auto' }}
                      textAlign="right"
                    >
                      {heatingFuelEmoji} {Math.round(home.spaceHeatingDemandKwh).toLocaleString()} kWh
                    </Text>
                  </HStack>
                </Box>
              )}
              {(home.nonHeatingElectricityKwh != null || nonHeatingGasKwh != null) && (
                <Box bg="gray.100" borderRadius="lg" p={{ base: 3, sm: 4 }}>
                  <HStack gap={{ base: 3, sm: 4 }} align="center" flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
                    <Box
                      w="40px" h="40px" flexShrink={0}
                      display="flex" alignItems="center" justifyContent="center"
                      borderRadius="xl" bg="white" boxShadow="xs"
                      fontSize="xl" lineHeight={1}
                    >
                      💡
                    </Box>
                    <Box flex={1} minW={0}>
                      <HStack gap={1.5} mb={0.5}>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">Other energy use</Text>
                        <HelpTip label="Everything else your home uses energy for — hot water, cooking, lights and appliances. Gas is used for hot water and cooking in most homes." />
                      </HStack>
                      <Text fontSize="xs" color="gray.500">Hot water, cooking, lights and appliances</Text>
                    </Box>
                    <VStack gap={1} align="end" flexShrink={0} w={{ base: 'full', sm: 'auto' }}>
                      {nonHeatingGasKwh != null && nonHeatingGasKwh > 0 && (
                        <HStack gap={2} align="baseline">
                          <Text fontSize="xs" color="gray.500">🔥 Gas</Text>
                          <Text fontSize="sm" fontWeight="bold" color="gray.900">
                            {Math.round(nonHeatingGasKwh).toLocaleString()} kWh
                          </Text>
                        </HStack>
                      )}
                      {home.nonHeatingElectricityKwh != null && (
                        <HStack gap={2} align="baseline">
                          <Text fontSize="xs" color="gray.500">⚡ Electricity</Text>
                          <Text fontSize="sm" fontWeight="bold" color="gray.900">
                            {Math.round(home.nonHeatingElectricityKwh).toLocaleString()} kWh
                          </Text>
                        </HStack>
                      )}
                    </VStack>
                  </HStack>
                </Box>
              )}
              {(totalGasKwh != null || totalElecKwh != null) && (
                <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="lg" p={{ base: 3, sm: 4 }}>
                  <HStack gap={{ base: 3, sm: 4 }} align="center" flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
                    <Box
                      w="40px" h="40px" flexShrink={0}
                      display="flex" alignItems="center" justifyContent="center"
                      borderRadius="xl" bg="white" boxShadow="xs"
                      fontSize="xl" lineHeight={1}
                    >
                      📊
                    </Box>
                    <Box flex={1} minW={0}>
                      <Text fontSize="sm" fontWeight="semibold" color="green.800">Energy used in a year</Text>
                      <Text fontSize="xs" color="green.700" mt={0.5}>Heating plus everything else</Text>
                    </Box>
                    <VStack gap={1} align="end" flexShrink={0} w={{ base: 'full', sm: 'auto' }}>
                      {totalGasKwh != null && (
                        <HStack gap={2} align="baseline">
                          <Text fontSize="xs" color="green.700">🔥 Gas</Text>
                          <Text fontSize="sm" fontWeight="bold" color="green.900">
                            {Math.round(totalGasKwh).toLocaleString()} kWh
                          </Text>
                        </HStack>
                      )}
                      {totalElecKwh != null && (
                        <HStack gap={2} align="baseline">
                          <Text fontSize="xs" color="green.700">⚡ Electricity</Text>
                          <Text fontSize="sm" fontWeight="bold" color="green.900">
                            {Math.round(totalElecKwh).toLocaleString()} kWh
                          </Text>
                        </HStack>
                      )}
                    </VStack>
                  </HStack>
                </Box>
              )}
              {home.upgradesCurrentEnergyCost != null && (
                <HStack
                  justify="space-between"
                  align={{ base: 'flex-start', sm: 'center' }}
                  flexDirection={{ base: 'column', sm: 'row' }}
                  gap={{ base: 1, sm: 4 }}
                  pt={4}
                  mt={2}
                  borderTop="1px solid"
                  borderColor="gray.100"
                >
                  <HStack gap={1.5}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Current annual energy bill estimated</Text>
                    <HelpTip label="An estimate of your total annual energy spend based on current usage and typical unit rates for your area." />
                  </HStack>
                  <Text fontSize={{ base: 'xl', sm: '2xl' }} fontWeight="bold" color="gray.900">
                    {fmt(home.upgradesCurrentEnergyCost, '£')}
                  </Text>
                </HStack>
              )}
            </VStack>
          </Box>
        </Box>

        {/* Section 3: Suggested improvements */}
        <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" overflow="hidden">
          <Box px={{ base: 4, sm: 5 }} pt={{ base: 5, sm: 6 }} pb={4}>
            <Heading size="md" color="gray.900">Suggested improvements</Heading>
            <Text fontSize="sm" color="gray.500" mt={1} lineHeight={1.6}>
              {pricesUnlocked
                ? 'The changes that could significantly lower your energy bills and their cost estimates.'
                : 'The changes that could significantly lower your energy bills. Get your free price estimates to see what they would cost.'}
            </Text>
          </Box>
          <Box px={{ base: 4, sm: 5 }} pb={5}>
            <VStack gap={2} align="stretch">
              <Box bg="gray.100" borderRadius="lg" p={{ base: 3, sm: 4 }}>
                <HStack gap={{ base: 3, sm: 4 }} align="start" flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
                  <Image src="/solar.png" alt="" w="40px" h="40px" flexShrink={0} borderRadius="xl" objectFit="cover" boxShadow="xs" />
                  <Box flex={1} minW={0}>
                    <HStack gap={1.5} mb={0.5}>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">Solar panels</Text>
                      <HelpTip label="Panels on your roof that turn daylight into electricity for your home." />
                    </HStack>
                    <Text fontSize="xs" color="gray.500">
                      {home.upgradesRecommendedPanels != null
                        ? `${home.upgradesRecommendedPanels} roof panels${home.upgradesSolarKw != null ? ` · ${home.upgradesSolarKw.toFixed(1)} kW system` : ''}`
                        : 'Panels fitted to your roof'}
                    </Text>
                  </Box>
                  {solarCost != null && (
                    pricesUnlocked ? (
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        color="gray.900"
                        flexShrink={0}
                        alignSelf="center"
                        w={{ base: 'full', sm: 'auto' }}
                        textAlign="right"
                      >
                        {fmt(solarCost, '£')}
                      </Text>
                    ) : (
                      <Box flexShrink={0} alignSelf="center" w={{ base: 'full', sm: 'auto' }} textAlign="right">
                        <LockedValue placeholder="£6,500" onReveal={revealLeadForm} />
                      </Box>
                    )
                  )}
                </HStack>
              </Box>
              <Box bg="gray.100" borderRadius="lg" p={{ base: 3, sm: 4 }}>
                <HStack gap={{ base: 3, sm: 4 }} align="start" flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
                  <Image src="/battery.png" alt="" w="40px" h="40px" flexShrink={0} borderRadius="xl" objectFit="cover" boxShadow="xs" />
                  <Box flex={1} minW={0}>
                    <HStack gap={1.5} mb={0.5}>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">Battery storage</Text>
                      <HelpTip label="A home battery stores extra solar electricity so you can use it later, such as in the evening." />
                    </HStack>
                    <Text fontSize="xs" color="gray.500">
                      Stores spare solar electricity
                      {home.upgradesBatteryKw != null && ` · ${home.upgradesBatteryKw.toFixed(1)} kWh battery`}
                    </Text>
                  </Box>
                  {batteryCost != null && (
                    pricesUnlocked ? (
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        color="gray.900"
                        flexShrink={0}
                        alignSelf="center"
                        w={{ base: 'full', sm: 'auto' }}
                        textAlign="right"
                      >
                        {fmt(batteryCost, '£')}
                      </Text>
                    ) : (
                      <Box flexShrink={0} alignSelf="center" w={{ base: 'full', sm: 'auto' }} textAlign="right">
                        <LockedValue placeholder="£3,200" onReveal={revealLeadForm} />
                      </Box>
                    )
                  )}
                </HStack>
              </Box>
              <Box bg="gray.100" borderRadius="lg" p={{ base: 3, sm: 4 }}>
                <HStack gap={{ base: 3, sm: 4 }} align="start" flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
                  <Image src="/heatpump.png" alt="" w="40px" h="40px" flexShrink={0} borderRadius="xl" objectFit="cover" boxShadow="xs" />
                  <Box flex={1} minW={0}>
                    <HStack gap={1.5} mb={0.5}>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">Air source heat pump</Text>
                      <HelpTip label="A heat pump uses electricity to take warmth from outside air and move it into your home." />
                    </HStack>
                    <Text fontSize="xs" color="gray.500">
                      {heatPumpSubline}
                    </Text>
                  </Box>
                  {heatPumpNetCost != null && (
                    <Box flexShrink={0} textAlign="right" alignSelf="center" w={{ base: 'full', sm: 'auto' }}>
                      {pricesUnlocked ? (
                        <>
                          {(heatPumpGrantApplied > 0 || heatPumpCashbackApplied > 0) && heatPumpGrossCost != null && (
                            <Text fontSize="xs" color="gray.400" textDecoration="line-through">
                              {fmt(heatPumpGrossCost, '£')}
                            </Text>
                          )}
                          {heatPumpGrantApplied > 0 && (
                            <Text fontSize="xs" fontWeight="medium" color="green.600">
                              −{fmt(heatPumpGrantApplied, '£')} grant
                            </Text>
                          )}
                          {heatPumpCashbackApplied > 0 && (
                            <Text fontSize="xs" fontWeight="medium" color="green.600">
                              −{fmt(heatPumpCashbackApplied, '£')} cashback
                            </Text>
                          )}
                          <Text fontSize="sm" fontWeight="bold" color="gray.900">
                            {fmt(heatPumpNetCost, '£')}
                          </Text>
                        </>
                      ) : (
                        <LockedValue placeholder="£7,500" onReveal={revealLeadForm} />
                      )}
                    </Box>
                  )}
                </HStack>
              </Box>

              {netUpgradeCost != null && (
                <HStack
                  justify="space-between"
                  align={{ base: 'flex-start', sm: 'center' }}
                  flexDirection={{ base: 'column', sm: 'row' }}
                  gap={{ base: 1, sm: 4 }}
                  pt={4}
                  mt={2}
                  borderTop="1px solid"
                  borderColor="gray.100"
                >
                  <HStack gap={1.5}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Total cost estimated to install</Text>
                    <HelpTip label="Estimated total installation cost for all recommended upgrades, after any applicable grants and cashback have been deducted. Exact costs will be confirmed during a free consultation." />
                  </HStack>
                  {pricesUnlocked ? (
                    <Text fontSize={{ base: 'xl', sm: '2xl' }} fontWeight="bold" color="gray.900">
                      {fmt(netUpgradeCost, '£')}
                    </Text>
                  ) : (
                    <LockedValue
                      placeholder="£12,400"
                      fontSize={{ base: 'xl', sm: '2xl' }}
                      onReveal={revealLeadForm}
                    />
                  )}
                </HStack>
              )}
            </VStack>
          </Box>
        </Box>

        {/* Lead capture: unlocks the price estimates above and below */}
        {leadCapture}

        {/* Section 4: After these improvements */}
        <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" overflow="hidden">
          <Box px={{ base: 4, sm: 5 }} pt={{ base: 5, sm: 6 }} pb={4}>
            <Heading size="md" color="gray.900">After these improvements</Heading>
            <Text fontSize="sm" color="gray.500" mt={1} lineHeight={1.6}>
              How your home could look with solar panels, a battery and a heat pump.
            </Text>
          </Box>
          <Box px={{ base: 4, sm: 5 }} pb={5}>
            <VStack gap={2} align="stretch">
              {home.upgradesSolarKwh != null && (
                <Box bg="gray.100" borderRadius="lg" p={{ base: 3, sm: 4 }}>
                  <HStack gap={{ base: 3, sm: 4 }} align="center" flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
                    <Box
                      w="40px" h="40px" flexShrink={0}
                      display="flex" alignItems="center" justifyContent="center"
                      borderRadius="xl" bg="white" boxShadow="xs"
                      fontSize="xl" lineHeight={1}
                    >
                      ☀️
                    </Box>
                    <Box flex={1} minW={0}>
                      <HStack gap={1.5} mb={0.5}>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">Solar power from your roof</Text>
                        <HelpTip label="How much free electricity your solar panels would generate over a full year, based on your roof size, orientation and local sunshine levels." />
                      </HStack>
                      <Text fontSize="xs" color="gray.500">Electricity your panels could make each year</Text>
                    </Box>
                    <Text
                      fontSize="sm"
                      fontWeight="bold"
                      color="gray.900"
                      flexShrink={0}
                      w={{ base: 'full', sm: 'auto' }}
                      textAlign="right"
                    >
                      {Math.round(home.upgradesSolarKwh).toLocaleString()} kWh/yr
                    </Text>
                  </HStack>
                </Box>
              )}
              {electrifiedTotalKwh != null && (
                <Box bg="gray.100" borderRadius="lg" p={{ base: 3, sm: 4 }}>
                  <HStack gap={{ base: 3, sm: 4 }} align="center" flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
                    <Box
                      w="40px" h="40px" flexShrink={0}
                      display="flex" alignItems="center" justifyContent="center"
                      borderRadius="xl" bg="white" boxShadow="xs"
                      fontSize="xl" lineHeight={1}
                    >
                      ⚡
                    </Box>
                    <Box flex={1} minW={0}>
                      <HStack gap={1.5} mb={0.5}>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">Electricity your home would use</Text>
                        <HelpTip label="Once your home is fully upgraded, it runs on electricity instead of gas. This is the total electricity it would need each year for heating, hot water, cooking, and everything else." />
                      </HStack>
                      <Text fontSize="xs" color="gray.500">Heating, hot water, cooking and everything else</Text>
                    </Box>
                    <Text
                      fontSize="sm"
                      fontWeight="bold"
                      color="gray.900"
                      flexShrink={0}
                      w={{ base: 'full', sm: 'auto' }}
                      textAlign="right"
                    >
                      {Math.round(electrifiedTotalKwh).toLocaleString()} kWh/yr
                    </Text>
                  </HStack>
                </Box>
              )}
              {home.upgradesEnergyCostAfter != null && (
                <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="lg" p={{ base: 3, sm: 4 }}>
                  <HStack gap={{ base: 3, sm: 4 }} align="center" flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
                    <Box
                      w="40px" h="40px" flexShrink={0}
                      display="flex" alignItems="center" justifyContent="center"
                      borderRadius="xl" bg="green.100" boxShadow="xs"
                      fontSize="xl" lineHeight={1}
                    >
                      📉
                    </Box>
                    <Box flex={1} minW={0}>
                      <HStack gap={1.5} mb={0.5}>
                        <Text fontSize="sm" fontWeight="semibold" color="green.800">Annual bill after improvements</Text>
                        <HelpTip label="Your estimated energy bill once the solar panels, battery and heat pump are all in place. Your home will run mainly on electricity, much of it generated for free by your own panels." />
                      </HStack>
                      <Text fontSize="xs" color="gray.600">Estimated energy cost with upgrades</Text>
                    </Box>
                    <Box textAlign="right" flexShrink={0} w={{ base: 'full', sm: 'auto' }}>
                      <Text fontSize="xl" fontWeight="bold" color="green.700">
                        {fmt(home.upgradesEnergyCostAfter, '£')}
                      </Text>
                      {home.upgradesAnnualSavings != null && (
                        <Text fontSize="xs" fontWeight="semibold" color="green.600">
                          {fmt(home.upgradesAnnualSavings, '£')} saved each year
                        </Text>
                      )}
                    </Box>
                  </HStack>
                </Box>
              )}
            </VStack>
          </Box>
        </Box>

        {/* Grants & financing */}
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" overflow="hidden">
          <Box px={{ base: 4, sm: 5 }} pt={{ base: 5, sm: 6 }} pb={4}>
            <Heading size="md" color="gray.900">Financing options</Heading>
          </Box>

          {/* Grants subsection */}
          <Box px={{ base: 4, sm: 5 }} pb={4}>
            <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={3}>Grants</Text>
            {noEpcFound && (
              <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="lg" p={4} mb={3}>
                <HStack align="flex-start" gap={3}>
                  <Text color="orange.500" fontWeight="bold" fontSize="lg" lineHeight={1}>⚠️</Text>
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" color="orange.900">
                      An EPC is required to qualify for these grants
                    </Text>
                    <Text fontSize="sm" color="orange.800" mt={1} lineHeight={1.5}>
                      Get an Energy Performance Certificate assessment done, then try this address again so we can
                      check your grant eligibility with the official certificate.
                    </Text>
                  </Box>
                </HStack>
              </Box>
            )}
            {grants.length === 0 ? (
              <Box bg="gray.50" borderRadius="lg" p={4} border="1px solid" borderColor="gray.200">
                <Text fontSize="sm" color="gray.600">
                  No specific grants were identified for this property. You may still be eligible for
                  national schemes. Visit{' '}
                  <a
                    href="https://www.gov.uk/improve-energy-efficiency"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'underline' }}
                  >
                    gov.uk/improve-energy-efficiency
                  </a>{' '}
                  for more information.
                </Text>
              </Box>
            ) : (
              <VStack gap={2} align="stretch">
                {grants.map((grant, i) => {
                  const e = grant.eligible
                  const showWarmHomesLocalGrantLink = e === 'yes'
                    && WARM_HOMES_LOCAL_GRANT_PATTERN.test(grant.name)
                  const cardStyle =
                    e === 'yes'     ? { bg: 'green.50',  border: 'green.200',  icon: '✓', iconColor: 'green.600', nameColor: 'gray.800',  descColor: 'gray.600' } :
                    e === 'no'      ? { bg: 'white',     border: 'gray.200',   icon: '✗', iconColor: 'red.500',   nameColor: 'gray.700',  descColor: 'gray.500' } :
                                      { bg: 'blue.50',   border: 'blue.200',   icon: '?', iconColor: 'blue.500',  nameColor: 'gray.800',  descColor: 'gray.600' }
                  const subtext =
                    e === 'no'      ? 'Based on your answers, you don\'t appear to qualify for this grant.' :
                    e === 'unknown' ? 'We couldn\'t confirm eligibility.' :
                                      null
                  return (
                    <Box
                      key={i}
                      bg={cardStyle.bg}
                      border="1px solid"
                      borderColor={cardStyle.border}
                      borderRadius="lg"
                      p={4}
                    >
                      <HStack
                        justify="space-between"
                        align={{ base: 'stretch', sm: 'flex-start' }}
                        gap={{ base: 2, sm: 4 }}
                        flexDirection={{ base: 'column', sm: 'row' }}
                      >
                        <VStack align="start" gap={1} flex={1} minW={0}>
                          <HStack gap={2}>
                            <Text color={cardStyle.iconColor} fontWeight="bold" fontSize="lg" lineHeight={1}>{cardStyle.icon}</Text>
                            <Text fontWeight="semibold" color={cardStyle.nameColor}>{grant.name}</Text>
                          </HStack>
                          <Text fontSize="sm" color={cardStyle.descColor}>{grant.description}</Text>
                          {showWarmHomesLocalGrantLink && (
                            <Text fontSize="sm" color="green.700" fontWeight="semibold">
                              <a
                                href={WARM_HOMES_LOCAL_GRANT_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: 'inherit', textDecoration: 'underline' }}
                              >
                                Find out more about the scheme
                              </a>
                            </Text>
                          )}
                          {subtext && (
                            <Text fontSize="xs" color={e === 'no' ? 'red.400' : 'blue.400'} fontStyle="italic">{subtext}</Text>
                          )}
                        </VStack>
                        {e === 'yes' && grant.maxAmountGbp > 0 && (
                          <Box bg="green.100" borderRadius="md" px={3} py={1} flexShrink={0} alignSelf="flex-start">
                            <Text fontSize="sm" fontWeight="bold" color="green.800" whiteSpace="nowrap">
                              Up to £{grant.maxAmountGbp.toLocaleString()}
                            </Text>
                          </Box>
                        )}
                        {e === 'no' && (
                          <Box
                            bg="white"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="md"
                            px={3}
                            py={1}
                            flexShrink={0}
                            alignSelf="flex-start"
                          >
                            <Text fontSize="sm" fontWeight="bold" color="red.700" whiteSpace="nowrap">
                              Not eligible
                            </Text>
                          </Box>
                        )}
                      </HStack>
                    </Box>
                  )
                })}
              </VStack>
            )}
          </Box>

          {/* Loans subsection */}
          <Box px={{ base: 4, sm: 5 }} pt={4} pb={5} borderTop="1px solid" borderColor="gray.100">
            <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={3}>Loans &amp; financing</Text>
            {hasNationwideGreenAdditionalBorrowing && (
              <Box
                bg="green.50"
                border="1px solid"
                borderColor="green.200"
                borderRadius="lg"
                p={4}
                mb={3}
              >
                <HStack
                  justify="space-between"
                  align={{ base: 'stretch', sm: 'flex-start' }}
                  gap={{ base: 2, sm: 4 }}
                  flexDirection={{ base: 'column', sm: 'row' }}
                >
                  <VStack align="start" gap={1} flex={1} minW={0}>
                    <HStack gap={2}>
                      <Text color="green.600" fontWeight="bold" fontSize="lg" lineHeight={1}>✓</Text>
                      <Text fontWeight="semibold" color="gray.800">
                        {NATIONWIDE_GREEN_ADDITIONAL_BORROWING.title}
                      </Text>
                    </HStack>
                    <Text fontSize="sm" color="gray.600">
                      Existing Nationwide mortgage customers could borrow more for eligible energy-efficient
                      improvements. With 0% interest on the additional borrowing for the first 2 or 5 years.{' '}
                      <a
                        href={NATIONWIDE_GREEN_ADDITIONAL_BORROWING.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'inherit', textDecoration: 'underline' }}
                      >
                        See details
                      </a>
                    </Text>
                  </VStack>
                  <Box bg="green.100" borderRadius="md" px={3} py={1} flexShrink={0} alignSelf="flex-start">
                    <Text fontSize="sm" fontWeight="bold" color="green.800" whiteSpace="nowrap">
                      {NATIONWIDE_GREEN_ADDITIONAL_BORROWING.amount}
                    </Text>
                  </Box>
                </HStack>
              </Box>
            )}
            <Box
              bg={hasGreenerHomeCashback ? 'green.50' : 'white'}
              border="1px solid"
              borderColor={hasGreenerHomeCashback ? 'green.200' : 'gray.200'}
              borderRadius="lg"
              p={4}
            >
              <HStack
                justify="space-between"
                align={{ base: 'stretch', sm: 'flex-start' }}
                gap={{ base: 2, sm: 4 }}
                flexDirection={{ base: 'column', sm: 'row' }}
              >
                <VStack align="start" gap={1} flex={1} minW={0}>
                  <HStack gap={2}>
                    <Text
                      color={hasGreenerHomeCashback ? 'green.600' : 'red.500'}
                      fontWeight="bold"
                      fontSize="lg"
                      lineHeight={1}
                    >
                      {hasGreenerHomeCashback ? '✓' : '✗'}
                    </Text>
                    <Text fontWeight="semibold" color="gray.800">
                      {greenerHomeCashbackScheme
                        ? `${greenerHomeCashbackScheme.schemeName} - Cashback`
                        : 'Green home cashback'}
                    </Text>
                  </HStack>
                  <Text fontSize="sm" color="gray.600">
                    Cashback may be available from your bank for eligible home energy improvements.
                    {greenerHomeCashbackScheme && (
                      <>
                        {' '}
                        <a
                          href={greenerHomeCashbackScheme.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'inherit', textDecoration: 'underline' }}
                        >
                          See details
                        </a>
                      </>
                    )}
                  </Text>
                  {!hasGreenerHomeCashback && (
                    <Text fontSize="xs" color="red.500" fontStyle="italic">
                      Eligible if you're with {eligibleCashbackBanks}.
                    </Text>
                  )}
                </VStack>
                <Box
                  bg={hasGreenerHomeCashback ? 'green.100' : 'white'}
                  border={hasGreenerHomeCashback ? undefined : '1px solid'}
                  borderColor={hasGreenerHomeCashback ? undefined : 'gray.200'}
                  borderRadius="md"
                  px={3}
                  py={1}
                  flexShrink={0}
                  alignSelf="flex-start"
                >
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    color={hasGreenerHomeCashback ? 'green.800' : 'red.700'}
                    whiteSpace="nowrap"
                  >
                    {greenerHomeCashbackScheme ? `£${greenerHomeCashbackScheme.amountGbp.toLocaleString()}` : 'Not eligible'}
                  </Text>
                </Box>
              </HStack>
            </Box>
          </Box>
        </Box>

        {/* Section 5: Financial summary */}
        <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" overflow="hidden">
          <Box px={{ base: 4, sm: 5 }} pt={{ base: 5, sm: 6 }} pb={4}>
            <HStack justify="space-between" gap={4} align="center">
              <Box>
                <Heading size="md" color="gray.900">At a glance</Heading>
                <Text fontSize="sm" color="gray.500" mt={1} lineHeight={1.6}>
                  {isLandlord
                    ? 'The estimated costs, returns and payback for improving this rental property.'
                    : 'What these improvements could mean for your bills and investment.'}
                </Text>
              </Box>
              <Box
                w="40px" h="40px" flexShrink={0}
                display="flex" alignItems="center" justifyContent="center"
                borderRadius="xl" bg="green.50" color="green.700"
                fontSize="xl" lineHeight={1}
              >
                ⚡
              </Box>
            </HStack>
          </Box>

          <Box px={{ base: 4, sm: 5 }} pb={5}>
            <Box bg="gray.50" borderRadius="lg" px={{ base: 3, sm: 4 }} py={4}>
              <HStack gap={3} align={{ base: 'stretch', sm: 'center' }} flexDirection={{ base: 'column', sm: 'row' }}>
                <Box flex={1} minW={0}>
                  <HStack gap={1.5}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Current annual bill</Text>
                    <HelpTip label="Your estimated annual energy spend before making the recommended improvements." />
                  </HStack>
                  <Text fontSize="2xl" fontWeight="bold" color="gray.900" mt={0.5}>
                    {fmt(home.upgradesCurrentEnergyCost, '£')}
                  </Text>
                </Box>
                <Text color="gray.400" fontSize="lg" flexShrink={0} transform={{ base: 'rotate(90deg)', sm: 'none' }} alignSelf="center">→</Text>
                <Box flex={1} minW={0} textAlign={{ base: 'left', sm: 'right' }}>
                  <HStack gap={1.5} justify={{ base: 'flex-start', sm: 'flex-end' }}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">With upgrades</Text>
                    <HelpTip label="Your estimated annual energy spend once the recommended improvements are installed." />
                  </HStack>
                  <Text fontSize="2xl" fontWeight="bold" color="green.700" mt={0.5}>
                    {fmt(home.upgradesEnergyCostAfter, '£')}
                  </Text>
                </Box>
              </HStack>
            </Box>

            <VStack gap={0} align="stretch" mt={2}>
              {([
                {
                  label: 'Annual savings',
                  value: fmt(home.upgradesAnnualSavings, '£'),
                  color: 'green.700',
                  help: 'How much less you could spend on energy each year once all the improvements are in place.',
                },
                {
                  label: 'Upgrade cost',
                  value: fmt(netUpgradeCost, '£'),
                  color: 'gray.900',
                  help: 'The estimated upfront cost of the recommended improvements after applicable grants and cashback are deducted.',
                  valueHelp: upgradeCostHelp,
                  lockedPlaceholder: '£12,400',
                },
                ...(isLandlord ? [{
                  label: 'ROI',
                  value: formatRoi(frontendRoi),
                  color: 'green.700',
                  help: 'The estimated annual energy savings as a percentage of the upgrade cost after applicable grants and cashback.',
                  lockedPlaceholder: '8.5%',
                }] : []),
                {
                  label: 'Payback',
                  value: formatPayback(frontendPaybackPeriod),
                  color: 'gray.900',
                  help: 'How long it could take for your energy savings to cover the upgrade cost. Savings after that point are a net benefit.',
                  lockedPlaceholder: '9 years',
                },
              ] as Array<{
                label: string
                value: string
                color: string
                help: string
                valueHelp?: string
                lockedPlaceholder?: string
              }>).map(item => (
                <HStack
                  key={item.label}
                  justify="space-between"
                  align={{ base: 'stretch', sm: 'center' }}
                  gap={{ base: 1, sm: 4 }}
                  flexDirection={{ base: 'column', sm: 'row' }}
                  py={3}
                  borderBottom="1px solid"
                  borderColor="gray.100"
                  _last={{ borderBottom: 'none', pb: 0 }}
                >
                  <HStack gap={1.5} minW={0}>
                    <Text fontSize="sm" color="gray.600">{item.label}</Text>
                    <HelpTip label={item.help} />
                  </HStack>
                  <HStack gap={1.5} justify={{ base: 'flex-start', sm: 'flex-end' }} flexShrink={0}>
                    {item.lockedPlaceholder && !pricesUnlocked ? (
                      <LockedValue placeholder={item.lockedPlaceholder} fontSize="md" onReveal={revealLeadForm} />
                    ) : (
                      <>
                        <Text fontSize="md" fontWeight="bold" color={item.color} textAlign={{ base: 'left', sm: 'right' }}>
                          {item.value}
                        </Text>
                        {item.valueHelp && (
                          <HelpTip label={item.valueHelp} />
                        )}
                      </>
                    )}
                  </HStack>
                </HStack>
              ))}
            </VStack>

            {showLandlordEpcUplift && (
              <Box
                mt={4}
                pt={4}
                borderTop="1px solid"
                borderColor="gray.200"
              >
                <HStack
                  justify="space-between"
                  gap={{ base: 3, sm: 4 }}
                  align={{ base: 'flex-start', sm: 'center' }}
                  flexDirection={{ base: 'column', sm: 'row' }}
                >
                  <Box>
                    <HStack gap={1.5}>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        EPC uplift
                      </Text>
                      <HelpTip label="GreenFlip's estimated EPC band after the recommended solar, battery and heating improvements." />
                    </HStack>
                    <Text fontSize="sm" color="green.700" fontWeight="semibold" mt={1}>
                      up to +12% increase in value on your property
                    </Text>
                  </Box>
                  <HStack gap={2} flexShrink={0}>
                    <EpcBadge rating={epcUpliftCurrentBand} />
                    <Text color="gray.400" fontSize="lg">→</Text>
                    <EpcBadge rating={epcUpliftEstimatedBand} />
                  </HStack>
                </HStack>
              </Box>
            )}
          </Box>
        </Box>

        {isLandlord && tenantPower?.eligible && (
          <Box
            bg="white"
            borderRadius="xl"
            border="1px solid"
            borderColor="gray.200"
            overflow="hidden"
          >
            <Box px={{ base: 4, sm: 5 }} pt={5} pb={3}>
              <Heading size="md" color="gray.900">Octopus Tenant Power</Heading>
              <Text fontSize="sm" color="gray.600" mt={1.5} lineHeight={1.5}>
                Octopus Tenant Power is a special electricity plan for rental properties with solar panels
                and a battery. Your tenants get cheaper electricity. Octopus pays you once a year for the
                solar panels and battery, as long as the property stays on the tariff.
              </Text>
            </Box>

            <VStack gap={0} align="stretch" px={{ base: 4, sm: 5 }}>
              {[
                {
                  key: 'tenant-saving',
                  label: (
                    <>
                      Your tenants save{' '}
                      <Text as="span" fontWeight="bold" color="gray.800">
                        {Math.round(tenantPower.tenant.discountRate * 100)}%
                      </Text>{' '}
                      on electricity per year
                    </>
                  ),
                  value: fmt(tenantPower.tenant.annualSaving, '£'),
                  color: 'gray.900',
                  help: `Your tenants pay less for their electricity. This could save them around ${fmt(tenantPower.tenant.annualSaving, '£')} each year.`,
                },
                {
                  key: 'octopus-payment',
                  label: 'Octopus pays you each year',
                  value: fmt(tenantPower.investor.totalAnnualPayment, '£'),
                  color: 'green.700',
                  help: 'This is money Octopus pays to you each year. You are not paying this amount to Octopus.',
                },
                {
                  key: 'tenant-power-roi',
                  label: 'ROI with Octopus Tenant Power',
                  value: formatRoi(tenantPowerRoi),
                  color: 'green.700',
                  help: 'This compares the amount Octopus pays you each year with the full upgrade cost shown above. Your tenants’ energy savings are not counted as money paid to you.',
                  lockedPlaceholder: '8.5%',
                },
                {
                  key: 'tenant-power-payback',
                  label: 'Payback with Octopus Tenant Power',
                  value: formatPayback(tenantPowerPaybackPeriod),
                  color: 'gray.900',
                  help: 'This is how long it could take for the yearly payments from Octopus to add up to the full upgrade cost shown above.',
                  lockedPlaceholder: '9 years',
                },
              ].map(item => (
                <HStack
                  key={item.key}
                  justify="space-between"
                  align={{ base: 'stretch', sm: 'center' }}
                  gap={{ base: 1, sm: 4 }}
                  flexDirection={{ base: 'column', sm: 'row' }}
                  py={3}
                  borderBottom="1px solid"
                  borderColor="gray.100"
                  _last={{ borderBottom: 'none' }}
                >
                  <HStack gap={1.5} minW={0}>
                    <Text fontSize="sm" color="gray.600">{item.label}</Text>
                    <HelpTip label={item.help} />
                  </HStack>
                  {'lockedPlaceholder' in item && item.lockedPlaceholder && !pricesUnlocked ? (
                    <LockedValue placeholder={item.lockedPlaceholder} fontSize="md" onReveal={revealLeadForm} />
                  ) : (
                    <Text
                      fontSize="md"
                      fontWeight="bold"
                      color={item.color}
                      textAlign={{ base: 'left', sm: 'right' }}
                      flexShrink={0}
                    >
                      {item.value}
                    </Text>
                  )}
                </HStack>
              ))}
            </VStack>

            <Box px={{ base: 4, sm: 5 }} pb={4}>
              <Text fontSize="xs" color="gray.500" lineHeight={1.4}>
                Octopus only pays for the solar panels and battery. ROI and payback still include the full
                upgrade cost, including the heat pump. The amount Octopus pays may change in the future.
              </Text>
            </Box>
          </Box>
        )}

        {/* Bottom CTA: scrolls back up to the lead-capture form while locked */}
        {!pricesUnlocked && (
          <Box bg="brand.500" borderRadius="xl" p={{ base: 4, sm: 6 }} color="white">
            <VStack gap={3} align="stretch">
              <Heading size="md" color="white">Ready to see the numbers?</Heading>
              <Text fontSize="sm" opacity={0.9}>
                Get your free, personalised price estimates for these improvements — no obligation.
              </Text>
              <Button
                bg="white"
                color="brand.500"
                _hover={{ bg: 'gray.100' }}
                size="lg"
                onClick={revealLeadForm}
                fontWeight="bold"
                h="auto"
                minH="48px"
                py={3}
                whiteSpace="normal"
                lineHeight="1.3"
              >
                Get my price estimates →
              </Button>
            </VStack>
          </Box>
        )}

        {showLandlordEmail && (
          <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" p={{ base: 4, sm: 5 }}>
            <Heading size="md" color="gray.900">Email to my landlord</Heading>
            <Text fontSize="sm" color="gray.500" mt={1} mb={4} lineHeight={1.6}>
              Send this assessment to your landlord so they can review the recommended improvements,
              costs and available support.
            </Text>
            <form
              onSubmit={(event) => {
                event.preventDefault()
                handleLandlordEmailSubmit()
              }}
            >
              <Field.Root invalid={Boolean(landlordEmailError)}>
                <Field.Label>Landlord's email address</Field.Label>
                <Grid templateColumns={{ base: '1fr', sm: '1fr auto' }} gap={3} w="100%">
                  <Input
                    type="email"
                    value={landlordEmail}
                    placeholder="landlord@example.com"
                    autoComplete="email"
                    onChange={(event) => {
                      setLandlordEmail(event.target.value)
                      setLandlordEmailError(null)
                    }}
                  />
                  <Button type="submit" bg="brand.500" color="white" _hover={{ opacity: 0.9 }} px={6}>
                    Send
                  </Button>
                </Grid>
                {landlordEmailError && <Field.ErrorText>{landlordEmailError}</Field.ErrorText>}
              </Field.Root>
            </form>
          </Box>
        )}

        {supportAvailable.length > 0 && (
          <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" overflow="hidden">
            <Box px={{ base: 4, sm: 5 }} pt={{ base: 5, sm: 6 }} pb={4}>
              <Heading size="md" color="gray.900">Support available</Heading>
              <Text fontSize="sm" color="gray.500" mt={1} lineHeight={1.6}>
                Local services and trusted resources that can help with your next steps.
              </Text>
            </Box>
            <VStack px={{ base: 4, sm: 5 }} pb={5} gap={3} align="stretch">
              {supportAvailable.map((item, index) => (
                <Box
                  key={`${item.name}-${index}`}
                  as="a"
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${item.name} (opens in a new tab)`}
                  display="flex"
                  alignItems="flex-start"
                  gap={{ base: 3, sm: 4 }}
                  p={{ base: 3, sm: 4 }}
                  borderRadius="lg"
                  color="inherit"
                  textDecoration="none"
                  _hover={{ bg: 'gray.50' }}
                >
                  <SupportThumbnail thumbnailUrl={item.thumbnailUrl} />
                  <Box flex={1} minW={0}>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">{item.name}</Text>
                    <Text fontSize="xs" color="gray.500" mt={1} lineHeight={1.5}>{item.description}</Text>
                  </Box>
                  <Text color="gray.400" fontSize="lg" flexShrink={0}>↗</Text>
                </Box>
              ))}
            </VStack>
          </Box>
        )}

        {/* Bottom navigation */}
        <Box
          display="flex"
          alignItems="stretch"
          justifyContent="space-between"
          flexDirection={{ base: 'column', sm: 'row' }}
          w="100%"
          gap={3}
          pb={4}
        >
          <Button
            bg="white"
            color="gray.700"
            border="1px solid"
            borderColor="gray.200"
            fontSize="sm"
            onClick={onBack}
            _hover={{ bg: 'gray.50', borderColor: 'gray.300' }}
            w={{ base: 'full', sm: 'auto' }}
          >
            ← Back to address
          </Button>
          <Button
            variant="ghost"
            color="gray.400"
            size="sm"
            onClick={onNewAddress}
            w={{ base: 'full', sm: 'auto' }}
          >
            Check another address
          </Button>
        </Box>

      </VStack>
    </Box>
  )
}

export default PilotResults
